import React, { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import type { User, AuthState, ApiResponse } from "../types/auth"

interface AuthContextType extends AuthState {
  loginWithGoogle: () => Promise<void>
  logout: () => void
  clearError: () => void
  resetAuthState: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// Your Ruby backend URL
const BACKEND_URL = ""

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null,
  })

  // Initialize Google OAuth without One Tap
  useEffect(() => {
    const initGoogleOAuth = () => {
      if (typeof window !== "undefined" && window.google) {
        window.google.accounts.id.initialize({
          client_id: "1010456288542-b2rvbe6og7aboj6nq08o5h0dfq0l737m.apps.googleusercontent.com",
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          // Disable One Tap completely
          use_fedcm_for_prompt: false,
        })

        // Check for existing auth token
        checkExistingAuth()
      }
    }

    // Load Google OAuth script
    if (typeof window !== "undefined" && !window.google) {
      const script = document.createElement("script")
      script.src = "https://accounts.google.com/gsi/client"
      script.async = true
      script.defer = true
      script.onload = initGoogleOAuth
      document.head.appendChild(script)
    } else {
      initGoogleOAuth()
    }
  }, [])

  // Check for existing auth token on mount
  const checkExistingAuth = async () => {
    const token = localStorage.getItem("auth_token")
    if (token) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        })

        if (response.ok) {
          const result: ApiResponse<User> = await response.json()

          if (result.success && result.data) {
            setAuthState({
              isAuthenticated: true,
              user: result.data,
              isLoading: false,
              error: null,
            })
          } else {
            // Invalid response format or unsuccessful
            localStorage.removeItem("auth_token")
            setAuthState((prev) => ({
              ...prev,
              isLoading: false,
              error: result.error || result.message || "Authentication failed",
            }))
          }
        } else {
          // Invalid token, remove it
          localStorage.removeItem("auth_token")
          setAuthState((prev) => ({ ...prev, isLoading: false }))
        }
      } catch (error) {
        console.error("Error checking existing auth:", error)
        localStorage.removeItem("auth_token")
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to verify authentication",
        }))
      }
    } else {
      setAuthState((prev) => ({ ...prev, isLoading: false }))
    }
  }

  const handleGoogleResponse = async (response: any) => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }))

      // Send the ID token to your Ruby backend under the 'token' field name
      const backendResponse = await fetch(`${BACKEND_URL}/api/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: response.credential, // Send ID token as 'token'
        }),
      })

      if (!backendResponse.ok) {
        throw new Error(`HTTP error! status: ${backendResponse.status}`)
      }

      const result: ApiResponse<{ user: User; auth_token: string }> = await backendResponse.json()

      if (!result.success || !result.data) {
        throw new Error(result.error || result.message || "Backend authentication failed")
      }

      const { user, auth_token } = result.data

      // Store the JWT token from your backend
      localStorage.setItem("auth_token", auth_token)

      setAuthState({
        isAuthenticated: true,
        user,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      console.error("Google authentication error:", error)
      const errorMessage = error instanceof Error ? error.message : "Failed to authenticate with server"
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))

      throw error // Re-throw to be caught by the calling function
    }
  }

  const loginWithGoogle = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!window.google) {
        const error = new Error("Google OAuth not loaded")
        setAuthState((prev) => ({
          ...prev,
          error: "Google OAuth not loaded",
        }))
        reject(error)
        return
      }

      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        // Create a temporary container for the Google button
        const tempContainer = document.createElement("div")
        tempContainer.style.position = "fixed"
        tempContainer.style.top = "-9999px"
        tempContainer.style.left = "-9999px"
        tempContainer.style.visibility = "hidden"
        document.body.appendChild(tempContainer)

        // Render the Google Sign-In button
        window.google.accounts.id.renderButton(tempContainer, {
          theme: "outline",
          size: "large",
          type: "standard",
          shape: "rectangular",
          text: "signin_with",
          logo_alignment: "left",
        })

        // Programmatically click the button
        setTimeout(() => {
          const button = tempContainer.querySelector('div[role="button"]') as HTMLElement
          if (button) {
            button.click()
          } else {
            reject(new Error("Could not trigger Google Sign-In"))
          }

          // Clean up the temporary container
          setTimeout(() => {
            if (document.body.contains(tempContainer)) {
              document.body.removeChild(tempContainer)
            }
          }, 1000)
        }, 100)

        // Set up authentication check
        const checkAuth = setInterval(() => {
          if (authState.isAuthenticated) {
            clearInterval(checkAuth)
            resolve()
          }
        }, 100)

        // Timeout after 30 seconds
        setTimeout(() => {
          clearInterval(checkAuth)
          if (!authState.isAuthenticated) {
            setAuthState((prev) => ({
              ...prev,
              isLoading: false,
              error: "Google login timed out",
            }))
            reject(new Error("Google login timed out"))
          }
        }, 30000)
      } catch (error) {
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
          error: "Google login failed",
        }))
        reject(new Error("Google login failed"))
      }
    })
  }

  const logout = async () => {
    try {
      // Remove local token
      localStorage.removeItem("auth_token")

      // Sign out from Google
      if (window.google) {
        window.google.accounts.id.disableAutoSelect()
      }

      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      console.error("Logout error:", error)
      setAuthState((prev) => ({
        ...prev,
        error: "Failed to logout",
        isLoading: false,
      }))
    }
  }

  const clearError = () => {
    setAuthState((prev) => ({ ...prev, error: null }))
  }

  // Function to reset auth state for retries
  const resetAuthState = () => {
    setAuthState((prev) => ({
      ...prev,
      isLoading: false,
      error: null,
    }))
  }

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        loginWithGoogle,
        logout,
        clearError,
        resetAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

// Extend Window interface for Google OAuth
declare global {
  interface Window {
    google: any
  }
}
